import type { ReactElement } from "react"
import { type FfnIconProps, IconMoon, IconSun } from "../icons/icons.js"
import "./ThemeToggle.css"

/**
 * The three colour schemes the token stylesheets project. Redeclared here
 * rather than imported: this package sits BELOW the one that owns the theme
 * atom and may not depend on it, and a toggle only needs the shape of the value
 * anyway, never the store that holds it.
 */
export type ThemeMode = "light" | "dark" | "hc"

/** The two schemes the toggle can land on — `hc` is reached from OS preferences. */
export type ThemeToggleTarget = "light" | "dark"

export interface ThemeToggleLabels {
  /** Word naming the light target. @default "Light" */
  readonly light?: string
  /** Word naming the dark target. @default "Dark" */
  readonly dark?: string
  /**
   * Builds the button's accessible name from the resolved target word.
   * @default (target) => `Switch to ${target.toLowerCase()} theme`
   */
  readonly action?: (target: string) => string
}

export interface ThemeToggleProps {
  /** The scheme currently applied to the document. */
  readonly theme: ThemeMode
  /** Called on press. The owner flips its own state; this component stores nothing. */
  readonly onToggle: () => void
  /** Overrides for the target word and the accessible-name sentence. */
  readonly labels?: ThemeToggleLabels
  /**
   * Also render the target word beside the glyph. @default false
   *
   * Ignored under `variant="pill"`, which is word-only by definition.
   */
  readonly showLabel?: boolean
  /**
   * Shape of the control, emitted as `data-variant` (the Button convention).
   * `ghost` matches a ghost Button so the toggle can sit in a Topbar action row;
   * `pill` is the outlined capsule an artifact document wants, where the control
   * is the only chrome on the page and a ghost button reads as nothing at all.
   * `pill` is word-only — no glyph — because that is what the shape is for.
   *
   * Inlined rather than a named exported union, matching `ButtonProps.variant`
   * (Button.tsx:5) — the barrel exports one type per family and this one is not
   * a value a consumer ever holds.
   *
   * @default "ghost"
   */
  readonly variant?: "ghost" | "pill"
}

/**
 * Icons name the TARGET, not the current mode: a sun means "go light". That
 * semantic is the load-bearing part and is unchanged — only the rendering moved
 * from a pair of sun/moon TEXT glyphs to the house icon seam (2026-08-14, Tabler
 * adoption), which is why this is now a map of components rather than strings.
 */
const GLYPHS: Record<ThemeToggleTarget, (props: FfnIconProps) => ReactElement> = {
  light: IconSun,
  dark: IconMoon
}

const DEFAULT_WORDS: Record<ThemeToggleTarget, string> = { light: "Light", dark: "Dark" }

const defaultAction = (target: string) => `Switch to ${target.toLowerCase()} theme`

/**
 * Resolve the scheme a press lands on. `hc` is an accessibility overlay rather
 * than a half of the toggle, so it takes the same branch `light` does and hands
 * the scheme back to the user instead of bouncing between an overlay and a
 * colour scheme. Exported so the app can flip its own state to exactly the mode
 * the button promised: this restates the `oppositeTheme` rule that the theme
 * store already encodes one layer up, which this package may not reach into, and
 * two hand-written copies of the rule are how they drift apart.
 */
export const themeToggleTarget = (theme: ThemeMode): ThemeToggleTarget =>
  theme === "dark" ? "light" : "dark"

/**
 * PRESENTATIONAL theme switch: it renders the target mode and reports a press.
 * It reads no store, writes no DOM attribute and persists nothing — the app owns
 * the theme value (an Effect Atom) and the single `applyTheme` DOM writer, both
 * of which live in a package layered ABOVE this one and therefore out of reach.
 * Three apps each carried a near-identical ~20-line copy of this button; this is
 * the one they share.
 *
 * Both the glyph and the optional word name the mode a press MOVES TO, which is
 * the convention a toggle button needs: the control describes its action, not
 * its state. The accessible name is a full sentence in real (visually hidden)
 * text rather than an `aria-label`, the Spinner house form — so it survives
 * translation tooling and is never silently replaced by a name computed
 * elsewhere. The visible word is `aria-hidden` when shown, so the name stays
 * exactly one sentence instead of "Dark Switch to dark theme".
 *
 * `variant` is the one appearance knob, on `data-variant` like Button's: the
 * `pill` shape is word-only, so it implies the word rather than making a caller
 * pass `showLabel` too, and the glyph goes out through the stylesheet. The
 * accessible sentence is identical in both shapes — a variant changes what the
 * control looks like, never what it is called.
 */
export function ThemeToggle({
  theme,
  onToggle,
  labels,
  showLabel = false,
  variant = "ghost"
}: ThemeToggleProps) {
  const target = themeToggleTarget(theme)
  const word = labels?.[target] ?? DEFAULT_WORDS[target]
  const action = (labels?.action ?? defaultAction)(word)
  // The pill IS the word, so it does not also require `showLabel`.
  const withWord = variant === "pill" || showLabel
  const Glyph = GLYPHS[target]
  return (
    <button
      type="button"
      className="FfnThemeToggle"
      data-variant={variant}
      // `data-mode`, NOT `data-theme`: the token stylesheets select the scheme
      // with a bare `[data-theme="dark"]` / `[data-theme="hc"]` (no `html`
      // prefix), so a button carrying `data-theme` re-declares that entire
      // scheme's tokens on its own subtree. A `theme="hc"` toggle on a dark page
      // therefore painted hc ink (#242b33) on the dark surface (#201f1d) at
      // 1.15:1 — caught by axe color-contrast, 2026-08-13. Scoping the token
      // selectors is a tokens-wave decision; a component must simply not stamp
      // the attribute the theme system owns.
      data-mode={theme}
      data-target={target}
      onClick={onToggle}
    >
      {/* The pill drops the glyph in the stylesheet ([data-variant="pill"]
          hides it) rather than here: the markup then has one shape whatever the
          variant, and the whole difference between the two looks lives in the
          one file that owns looks. It is `aria-hidden` either way, so hiding it
          changes nothing about the accessible name. */}
      <span className="FfnThemeToggleGlyph" aria-hidden="true">
        <Glyph />
      </span>
      {withWord && (
        <span className="FfnThemeToggleWord" aria-hidden="true">
          {word}
        </span>
      )}
      <span className="FfnThemeToggleSr">{action}</span>
    </button>
  )
}
